import { useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import type { Recipe } from '../../types/inventory';
import type { UserRole } from '../../types/auth';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import { scrollParentOf } from '../pos/scrollParent';
import {
  PRODUCT_STEPS,
  continueLabel,
  costText,
  firstProblem,
  initialDraft,
  nextScreen,
  previousScreen,
  priceText,
  problemPlace,
  productPayload,
  progressMarks,
  stepPill,
  stepProblem,
  storesText,
  taxText,
  toggleAllStores,
  toggleStore,
  type ProductDraft,
  type ProductScreen,
  type ProductStep,
  type StoreOption,
} from '../../utils/newProduct';
import { ProductAnswerRow, ProductHeader } from './ProductParts';
import { ProductReview } from './ProductReview';
import { STEP_LOOK, StepCard, type RecipesState, type StepProblem } from './ProductSteps';
import { useProductCreate } from './useProductCreate';

/**
 * Rediseño · Bloque E — «Nuevo producto» (P-029): cinco preguntas, una revisión y «Crear producto».
 *
 * Lo que NO cambió: se crea con la misma llamada de siempre (`createOtherSaleProduct`, el
 * `buildProductPayload` del alta anterior), una sola vez y solo al tocar «Crear producto». No hay
 * borradores ni envíos parciales. El servidor sigue decidiendo quién puede crear y en qué tiendas.
 * La llamada vive en `useProductCreate` (ONB-004), que comparte con su variante mínima, el alta rápida.
 *
 * Una sola alta a la vez:
 *   · Mientras se crea, el botón queda ocupado, «Cambiar», «Atrás» y «Volver» no responden y el Atrás
 *     del teléfono queda consumido. La guarda es una referencia, no solo el estado: dos toques en el
 *     mismo instante no mandan dos.
 *   · «Creado» solo aparece con la respuesta del servidor. Ya creado no hay vuelta a la captura: lo
 *     que queda es salir.
 *   · Si la pantalla se va antes de la respuesta, la respuesta no hace nada aquí (ni éxito ni error).
 *   · Sin red o con un error del servidor no se sabe si se creó: se dice así, no «falló». Reintentar no
 *     duplica: el nombre es único por negocio y el servidor lo rechazaría.
 *
 * El Atrás del teléfono hace lo mismo que «Atrás» de la pantalla: un paso hacia atrás y, desde el
 * primero, salir. Lo registra la página en la pila desde que se abre (`phoneBack`); con un diálogo o el
 * menú abierto encima, responden ellos primero.
 */
export function ProductWizard({ role, stores, recipes, recipeList, onRetryRecipes, onExit, phoneBack }: {
  role: UserRole | undefined;
  stores: readonly StoreOption[];
  recipes: RecipesState;
  /** Las recetas tal como llegaron, para nombrar la elegida. */
  recipeList: readonly Recipe[];
  onRetryRecipes: () => void;
  onExit: () => void;
  /** Lo que hace el Atrás del teléfono mientras el asistente está en pantalla. */
  phoneBack: MutableRefObject<(() => void) | null>;
}) {
  const context = { role, stores };
  const [draft, setDraft] = useState<ProductDraft>(() => initialDraft(role));
  const [screen, setScreen] = useState<ProductScreen>('name');
  const [confirmed, setConfirmed] = useState<ReadonlySet<ProductStep>>(() => new Set());
  const [problem, setProblem] = useState<(StepProblem & { step: ProductStep }) | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(false);
  const [recipeQuery, setRecipeQuery] = useState('');
  const { create: createProduct, savingRef, createdRef } = useProductCreate();
  const rootRef = useRef<HTMLElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  // A dónde va el foco después del próximo cambio de pantalla: a la pregunta, al problema o al aviso de
  // éxito. `back` es la pregunta a la que se regresó: la tarjeta, nunca su campo (ver `back`).
  const focusNext = useRef<'screen' | 'back' | 'problem' | 'done' | null>(null);

  // P-029 deja el pie abajo aunque la pregunta sea corta: la pantalla ocupa al menos lo que queda visible
  // del área de contenido del shell —debajo de su barra y sin su margen de abajo— y la pila crece hasta
  // ahí. Con más contenido todo se desplaza y el pie sigue pegado. Se vuelve a medir si el área cambia de
  // alto (girar, el teclado). Lo encontró la revisión independiente.
  useEffect(() => {
    const root = rootRef.current;
    const scroller = root ? scrollParentOf(root) : null;
    if (!root || !scroller) return undefined;
    const fit = () => {
      let below = 0;
      for (let node = root.parentElement; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        below += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
        if (node === scroller) break;
      }
      const top = root.getBoundingClientRect().top - scroller.getBoundingClientRect().top - scroller.clientTop + scroller.scrollTop;
      root.style.minHeight = `${Math.max(0, Math.floor(scroller.clientHeight - top - below))}px`;
    };
    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(scroller);
    window.addEventListener('resize', fit);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', fit);
      root.style.minHeight = '';
    };
  }, []);

  const busy = saving;

  const goTo = (next: ProductScreen, focus: 'screen' | 'back' | 'problem' = 'screen') => {
    focusNext.current = focus;
    setScreen(next);
  };

  const update = (patch: Partial<ProductDraft>) => {
    if (savingRef.current || createdRef.current) return;
    setDraft((current) => ({ ...current, ...patch }));
    setProblem(null);
    setReviewError(null);
  };

  const next = () => {
    if (screen === 'review' || savingRef.current) return;
    const found = stepProblem(screen, draft, context);
    if (found) {
      setProblem({ step: screen, place: problemPlace(screen, found), message: found });
      focusNext.current = 'problem';
      return;
    }
    const nowConfirmed = new Set(confirmed).add(screen);
    setConfirmed(nowConfirmed);
    setProblem(null);
    goTo(nextScreen(screen, nowConfirmed));
  };

  const back = () => {
    if (savingRef.current) return;
    if (createdRef.current) { onExit(); return; }
    const previous = previousScreen(screen);
    if (!previous) { onExit(); return; }
    setProblem(null);
    setReviewError(null);
    // Bloque G: al regresar, el foco va a la tarjeta y no al campo, así el teclado no vuelve a salir en
    // cada paso. En el teléfono, cada vez que salía había que tocar Atrás dos veces: una para cerrarlo y
    // otra para regresar (prueba en teléfono de P-070; decisión del dueño, 2026-10-07).
    goTo(previous, 'back');
  };

  const change = (step: ProductStep) => {
    if (savingRef.current || createdRef.current) return;
    setProblem(null);
    setReviewError(null);
    goTo(step);
  };

  const create = async () => {
    if (savingRef.current || createdRef.current) return;
    // P-029 `create`: antes de mandar nada, la primera respuesta que no pasa lleva a su paso.
    const pending = firstProblem(draft, context);
    if (pending) {
      setProblem({ step: pending.step, place: problemPlace(pending.step, pending.problem), message: pending.problem });
      goTo(pending.step, 'problem');
      return;
    }
    await createProduct(productPayload(draft, role, stores), 'No pudimos crear el producto.', {
      start: () => {
        setSaving(true);
        setReviewError(null);
      },
      created: () => {
        setCreated(true);
        focusNext.current = 'done';
      },
      failed: (failure) => {
        if (failure.screen === 'review') {
          setReviewError(failure.message);
        } else {
          const place = failure.screen === 'recipe' || failure.screen === 'tax' ? 'sub' : 'question';
          setProblem({ step: failure.screen, place, message: failure.message });
          goTo(failure.screen, 'problem');
        }
      },
      settled: () => setSaving(false),
    });
  };

  // El Atrás del teléfono: lo mismo que «Atrás»; mientras se crea, consumido sin hacer nada.
  useLayoutEffect(() => {
    phoneBack.current = () => { if (!savingRef.current) back(); };
  });
  useLayoutEffect(() => () => { phoneBack.current = null; }, [phoneBack]);

  // Al cambiar de pantalla, la pregunta nueva a la vista y el foco donde sirve: en el campo de texto
  // (como P-029), en la tarjeta si la respuesta son opciones o si se regresó a ella, en el problema si
  // lo hay, o en el aviso de éxito. Nada se enfoca al abrir la pantalla.
  useEffect(() => {
    const request = focusNext.current;
    focusNext.current = null;
    const root = rootRef.current;
    if (!request || !root) return;
    if (request === 'done') {
      statusRef.current?.focus({ preventScroll: true });
      return;
    }
    const card = root.querySelector<HTMLElement>(screen === 'review' ? '.n3-product__review' : '.n3-product__card');
    if (!card) return;
    let target: HTMLElement | null = card;
    if (request === 'problem') {
      const area = problem?.place === 'sub' ? card.querySelector<HTMLElement>('.n3-product__sub') : card;
      target = area?.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled])') ?? card;
    } else if (request === 'screen' && (screen === 'name' || screen === 'price')) {
      target = card.querySelector<HTMLElement>('input') ?? card;
    }
    target.focus({ preventScroll: true });
    // Solo se desplaza si la pregunta no se ve entera entre el borde de arriba y el pie: entonces queda
    // arriba, como en P-029. Si ya se ve, la vista no se mueve.
    const scroller = scrollParentOf(root);
    if (scroller) {
      const area = scroller.getBoundingClientRect();
      const foot = root.querySelector<HTMLElement>('.n3-product__foot');
      const lower = foot && getComputedStyle(foot).position === 'sticky' ? Math.min(foot.getBoundingClientRect().top, area.bottom) : area.bottom;
      const box = card.getBoundingClientRect();
      if (box.top < area.top || box.bottom > lower) scroller.scrollTop += box.top - area.top - 8;
    }
  }, [screen, problem, created]);

  const storesLabel = storesText(draft, stores);
  // P-071: el cuarto paso es el costo («¿Cómo quieres llevar el costo?»); conserva el id `recipe`.
  const costLabel = costText(draft, recipeList);
  const taxLabel = taxText(draft);
  const answers = screen === 'review' ? [] : PRODUCT_STEPS.filter((step) => step !== screen && confirmed.has(step));
  const valueOf = (step: ProductStep) => ({
    name: draft.name.trim(),
    price: priceText(draft),
    stores: storesLabel,
    recipe: costLabel,
    tax: taxLabel,
  }[step]);

  return (
    <section aria-labelledby="product-title" className="n3-product" ref={rootRef}>
      <ProductHeader backDisabled={busy} marks={progressMarks(screen, confirmed)} onBack={back} pill={stepPill(screen)} />

      <div className="n3-product__stack">
        {answers.length > 0 && (
          <section aria-label="Tus respuestas" className="n3-product__answers">
            {answers.map((step) => (
              <ProductAnswerRow
                changeDisabled={busy}
                icon={STEP_LOOK[step].icon}
                key={step}
                label={STEP_LOOK[step].label}
                onChange={() => change(step)}
                tone={STEP_LOOK[step].tone}
                value={valueOf(step)}
              />
            ))}
          </section>
        )}

        {screen === 'review'
          ? <ProductReview busy={busy} costLabel={costLabel} created={created} draft={draft} onChange={change} storesLabel={storesLabel} taxLabel={taxLabel} />
          : (
            <StepCard
              disabled={busy}
              draft={draft}
              key={screen}
              onDraft={update}
              onEnter={next}
              onRecipeQuery={setRecipeQuery}
              onRetryRecipes={onRetryRecipes}
              onToggleAll={() => update(toggleAllStores(draft))}
              onToggleStore={(storeId) => update(toggleStore(draft, storeId, stores))}
              ownerAll={role === 'OWNER'}
              problem={problem && problem.step === screen ? problem : null}
              recipeQuery={recipeQuery}
              recipes={recipes}
              step={screen}
              stores={stores}
            />
          )}
      </div>

      <div className="n3-product__foot">
        {screen === 'review' && reviewError && (
          <p className="n3-product__problem n3-product__problem--foot" role="alert">
            <Icon name="warning" size={16} />
            <span>{reviewError}</span>
          </p>
        )}
        {created && (
          <p className="n3-product__done" ref={statusRef} role="status" tabIndex={-1}>
            <span aria-hidden="true" className="n3-product__done-icon"><Icon name="success" size={18} /></span>
            <span>Producto creado.</span>
          </p>
        )}
        {screen === 'review'
          ? (
            <Button
              className={cx('n3-product__cta', created && 'n3-product__cta--done')}
              disabled={created}
              fullWidth
              iconStart={created ? <Icon name="check" size={18} /> : undefined}
              loading={saving}
              loadingLabel="Creando…"
              onClick={() => void create()}
              size="lg"
              variant="primary"
            >
              {created ? 'Creado' : 'Crear producto'}
            </Button>
          )
          : (
            <Button className="n3-product__cta" fullWidth onClick={next} size="lg" variant="primary">
              {continueLabel(screen, confirmed)}
            </Button>
          )}
        {screen === 'review' && (
          <Button className="n3-product__second" disabled={busy} fullWidth onClick={back} variant="ghost">Volver</Button>
        )}
      </div>
    </section>
  );
}
